import * as THREE from 'three';
import { CARD_WIDTH, CARD_HEIGHT, CARD_THICKNESS } from '../cardMesh';

// ── Airborne wind ────────────────────────────────────────────────
//
// Turbulent wind effect on the card surface. No center pivot, no
// radial spokes. Built around iterative sin/cos UV displacement, a
// classic trick from fluid-sim viewers / Genshin Anemo / ShaderToy
// wind shaders for producing organic divergence-free flow fields
// without the singularity that "vortex around a center" approaches
// inevitably introduce.
//
// Two coordinated systems on a single lay-flat group:
//
//   1. SWIRL. Stacked horizontal planes confined to the card
//      interior, sampling FBM noise at coordinates that have been
//      iteratively distorted by perpendicular sin/cos waves. Each
//      iteration adds curl-like turbulence. Threshold + irregular
//      gust envelope produce wispy curling streaks that come and go
//      across the card surface.
//
//   2. DUST MOTES. 20 small soft-halo particles whose positions
//      pass through the SAME displacement function. They visibly
//      ride the same flow field as the shader wisps. Wisp and
//      mote motion is coherent because they share a velocity field.
//
// Pale blue-cyan palette with NormalBlending. Air diffuses light,
// it doesn't glow. The wind tints the card rather than painting on
// top of it.

// ── Swirl constants ──────────────────────────────────────────────
const LAYER_COUNT = 4;
const RISE_HEIGHT = 0.2;
const PLANE_W = CARD_WIDTH * 1.1;
const PLANE_H = CARD_HEIGHT * 1.1;

// ── Mote constants ───────────────────────────────────────────────
//
// Particles orbit a wandering swirl center. Tornado-like vortex
// rather than straight drift through the card. Long lifetimes +
// per-particle gust phases mean the swarm always has SOME visible
// motes but rarely all at once: gusts roll across the field so the
// effect breathes instead of being a constant particle haze.
const MOTE_COUNT = 26;
const MOTE_LIFETIME = 4.5;
const MOTE_SIZE_MIN = 0.16;
const MOTE_SIZE_MAX = 0.42;
// Orbit radii. Motes occupy a band from the swirl center out to
// about 84% of half-card-width. Inner orbits feel like the eye of
// the vortex, outer like wind sheet wrapping around it.
const MOTE_ORBIT_MIN = CARD_WIDTH * 0.05;
const MOTE_ORBIT_MAX = CARD_WIDTH * 0.42;

let _sharedMoteTexture = null;

function getMoteTexture() {
  if (_sharedMoteTexture) return _sharedMoteTexture;
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  const cx = size / 2;
  const cy = size / 2;

  ctx.clearRect(0, 0, size, size);

  const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, size * 0.5);
  halo.addColorStop(0.00, 'rgba(220,235,250,0.55)');
  halo.addColorStop(0.20, 'rgba(200,220,240,0.32)');
  halo.addColorStop(0.50, 'rgba(170,200,225,0.10)');
  halo.addColorStop(1.00, 'rgba(170,200,225,0.00)');
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, size, size);

  const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, size * 0.14);
  core.addColorStop(0.0, 'rgba(245,250,255,1.0)');
  core.addColorStop(0.5, 'rgba(225,238,250,0.7)');
  core.addColorStop(1.0, 'rgba(210,230,245,0.0)');
  ctx.fillStyle = core;
  ctx.beginPath();
  ctx.arc(cx, cy, size * 0.14, 0, Math.PI * 2);
  ctx.fill();

  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  _sharedMoteTexture = tex;
  return tex;
}

// ── Shared flow displacement (used by both shaders) ──────────────
//
// Iterative perpendicular-sin/cos warp. Each iteration looks at the
// CURRENT distorted coord (so the warp is self-referential and the
// curling compounds), adds a sin perpendicular to one axis and a
// cos perpendicular to the other. The k_x / k_y frequencies and the
// per-iteration phase offset break up any obvious periodicity. Time
// advances the phase so the whole field flows.
//
// No `(p.x, p.y) / r` or `atan(p.y, p.x)` anywhere. There's no
// privileged center, hence no spokes.
const FLOW_DISPLACE_GLSL = /* glsl */ `
  vec2 flowDisplace(vec2 p, float t) {
    vec2 d = p;
    for (int i = 0; i < 3; i++) {
      float phase = t * 0.42 + float(i) * 1.3;
      d += vec2(
        sin(d.y * 0.55 + phase),
        cos(d.x * 0.52 + phase + 0.7)
      ) * 0.75;
    }
    return d;
  }
`;

// ── Swirl shaders ────────────────────────────────────────────────

const SWIRL_VERT = /* glsl */ `
  uniform float uLayerT;

  varying vec2  vLocalXY;
  varying float vHeight;

  void main() {
    vLocalXY = position.xy;
    vHeight = uLayerT;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const SWIRL_FRAG = /* glsl */ `
  precision highp float;

  uniform float uTime;
  uniform vec2  uCardSize;
  uniform float uLayerDensity;
  uniform float uFlowSpeed;
  uniform vec3  uColorCore;
  uniform vec3  uColorTint;

  varying vec2  vLocalXY;
  varying float vHeight;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i),                hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }
  float fbm(vec2 p) {
    float sum = 0.0;
    float amp = 0.5;
    for (int i = 0; i < 4; i++) {
      sum += amp * vnoise(p);
      p = p * 2.05 + vec2(1.7, 9.2);
      amp *= 0.55;
    }
    return sum;
  }

  ${FLOW_DISPLACE_GLSL}

  void main() {
    vec2 p = vLocalXY;

    // INSIDE-card envelope. The wind lives on the card surface.
    vec2 q = abs(p) - uCardSize * 0.5;
    float sdf = length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0);
    float insideMask = smoothstep(0.0, -2.0, sdf);
    if (insideMask < 0.005) discard;

    // Distort the sampling coord with the iterative sin/cos warp.
    // Per-layer phase offset (vHeight * 5.7) shifts each plane's
    // flow field slightly so the stack reads with depth instead of
    // as four pancakes of the same pattern.
    float layerPhase = vHeight * 5.7;
    vec2 displaced = flowDisplace(p * 0.45, uTime * uFlowSpeed + layerPhase);

    // Sample FBM noise at the displaced position. Because flowDisplace
    // is self-referential, neighbouring pixels get warped to very
    // different sample positions. This is what visually transforms
    // the noise into curling streaks.
    float n = fbm(displaced * 0.6);

    // Crisp-ish wisp threshold. Wind streaks look more like flowing
    // lines than fog blobs.
    float threshold = 0.36 + vHeight * 0.08;
    float wisps = smoothstep(threshold, 0.78, n);

    // Irregular gust envelope. Slow modulation that varies across
    // the card so different regions gust at different times.
    float gustPhase = uTime * 0.45 + vHeight * 3.1
                    + sin(p.x * 0.3) * 1.2
                    + cos(p.y * 0.28) * 0.9;
    float gust = sin(gustPhase);
    gust = smoothstep(-0.2, 0.7, gust);

    float intensity = insideMask * wisps * gust * uLayerDensity;
    if (intensity < 0.004) discard;

    vec3 col = mix(uColorCore, uColorTint, n);
    col *= mix(0.82, 1.0, intensity);

    gl_FragColor = vec4(col, intensity);
  }
`;

// ── Mote shaders ─────────────────────────────────────────────────

const MOTE_VERT = /* glsl */ `
  uniform float uTime;
  uniform float uLifetime;
  uniform float uSizeMin;
  uniform float uSizeMax;
  uniform float uOrbitSpeed;
  uniform float uGustFreq;
  uniform vec2  uCardSize;

  // Per-instance: starting angle around the (wandering) swirl
  // center, orbit radius, height, spin direction, seed, phase,
  // lifetime mult.
  attribute float aStartAngle;
  attribute float aOrbitRadius;
  attribute float aBaseZ;
  attribute float aSpinDir;
  attribute float aSeed;
  attribute float aPhase;
  attribute float aLifeMult;

  varying vec2  vUv;
  varying float vAlpha;
  varying float vSeed;

  void main() {
    float lifetime = uLifetime * aLifeMult;
    float life = mod(uTime / lifetime + aPhase, 1.0);

    // Wandering swirl center. Two decoupled low-frequency drifts
    // (0.07 and 0.085, prime-coprime so the lissajous never lines
    // up) keep the vortex sliding lazily across the card surface
    // instead of pinned to the geometric center. ±12% of card dims
    // is enough to read as "the wind is moving around" without the
    // center wandering off the silhouette.
    vec2 swirlCenter = vec2(
      cos(uTime * 0.07) * uCardSize.x * 0.12,
      sin(uTime * 0.085 + 1.0) * uCardSize.y * 0.12
    );

    // Per-particle angular velocity variance. Most particles spin
    // the same direction (cohesive vortex); a fraction spin counter
    // for visual chaos. uOrbitSpeed is the calm-tornado baseline.
    float angVel = uOrbitSpeed * (0.65 + aSeed * 0.7) * aSpinDir;

    // Orbit angle uses uTime DIRECTLY, not life. So orbits don't
    // visibly snap when a particle's lifecycle wraps. Only alpha
    // cycles; position is smooth and continuous.
    float angle = aStartAngle + uTime * angVel;

    // Radius shrinks subtly over lifetime. The particle gets
    // gently pulled inward, characteristic of a true vortex.
    float radius = aOrbitRadius * (1.0 - life * 0.35);

    // Tiny vertical bob so the swarm doesn't read as perfectly
    // flat. Within the stack-occlusion-safe band.
    float zBob = sin(uTime * 0.4 + aSeed * 4.0) * 0.03;

    vec3 pos = vec3(
      swirlCenter.x + cos(angle) * radius,
      swirlCenter.y + sin(angle) * radius,
      aBaseZ + zBob
    );

    vec4 mvCenter = modelViewMatrix * vec4(pos, 1.0);

    // Slow twinkle so individual motes don't have uniform brightness.
    float twinkleFreq = 0.5 + aSeed * 1.1;
    float twinkleRaw  = 0.5 + 0.5 * sin(uTime * twinkleFreq + aSeed * 31.0);
    float twinkle = pow(twinkleRaw, 1.5);

    vec2 corner = position.xy;
    float baseSize = mix(uSizeMin, uSizeMax, fract(aSeed * 7.13));
    float size = baseSize * (0.7 + 0.3 * twinkle);
    mvCenter.xy += corner * size;

    gl_Position = projectionMatrix * mvCenter;

    // Lifecycle envelope. Long fade in, long sustain, long fade out.
    float fadeIn  = smoothstep(0.0, 0.25, life);
    float fadeOut = 1.0 - smoothstep(0.7, 1.0, life);

    // ★ Per-particle gust phase. The vortex visibility pulses in
    // waves. Each particle's gate is offset by its seed so the
    // wave is staggered across the swarm rather than turning every
    // mote on/off in unison. The smoothstep keeps gusts sharp
    // (bursts) rather than slow sine pulses.
    float gust = sin(uTime * uGustFreq + aSeed * 6.0);
    gust = smoothstep(-0.1, 0.6, gust);

    // Combined alpha: lifecycle × twinkle × gust × baseline.
    // The 0.15 baseline means even at gust=0 a mote isn't fully
    // invisible. Just very dim. Eliminates the "did it disappear?"
    // jank without losing the gust-pulse identity.
    vAlpha = fadeIn * fadeOut * twinkle * (0.15 + 0.85 * gust);

    vUv = uv;
    vSeed = aSeed;
  }
`;

const MOTE_FRAG = /* glsl */ `
  precision highp float;

  uniform sampler2D uTexture;
  uniform vec3 uColorCore;
  uniform vec3 uColorTint;

  varying vec2  vUv;
  varying float vAlpha;
  varying float vSeed;

  void main() {
    vec4 tex = texture2D(uTexture, vUv);
    float a = tex.a * vAlpha;
    if (a < 0.003) discard;
    vec3 col = mix(uColorCore, uColorTint, fract(vSeed * 31.713));
    gl_FragColor = vec4(col, a);
  }
`;

export function createAirborneWindEffect() {
  const group = new THREE.Group();
  group.frustumCulled = false;

  const tSeed = Math.random() * 100;

  // ── SWIRL: stacked horizontal noise planes ──────────────────────
  const swirlGeometry = new THREE.PlaneGeometry(PLANE_W, PLANE_H);
  const swirlMaterials = [];

  for (let i = 0; i < LAYER_COUNT; i++) {
    const layerT = i / Math.max(LAYER_COUNT - 1, 1);
    const layerHeight = layerT * RISE_HEIGHT;
    const layerDensity = Math.exp(-1.6 * layerT) * 0.34;

    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: tSeed },
        uCardSize: { value: new THREE.Vector2(CARD_WIDTH, CARD_HEIGHT) },
        uLayerT: { value: layerT },
        uLayerDensity: { value: layerDensity },
        // Flow advection speed. The iterative displacement is mostly
        // shape-defining; this controls how fast that shape flows.
        // 0.55 reads as a calm steady breeze; 1.2+ becomes stormy.
        uFlowSpeed: { value: 0.55 },
        uColorCore: { value: new THREE.Color(0xa8c4dc) },
        uColorTint: { value: new THREE.Color(0xeaf2f8) },
      },
      vertexShader: SWIRL_VERT,
      fragmentShader: SWIRL_FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.NormalBlending,
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(swirlGeometry, material);
    mesh.frustumCulled = false;
    // Flat-to-card so stacked cards occlude the whole effect.
    mesh.position.z = layerHeight + CARD_THICKNESS / 2 + 0.05;
    mesh.renderOrder = 22 + i;
    group.add(mesh);
    swirlMaterials.push(material);
  }

  // ── MOTES: dust particles riding the flow field ─────────────────
  const moteQuad = new THREE.PlaneGeometry(1, 1);
  const moteGeometry = new THREE.InstancedBufferGeometry();
  moteGeometry.index = moteQuad.index;
  moteGeometry.setAttribute('position', moteQuad.getAttribute('position'));
  moteGeometry.setAttribute('uv', moteQuad.getAttribute('uv'));
  moteQuad.dispose();

  const startAngles = new Float32Array(MOTE_COUNT);
  const orbitRadii  = new Float32Array(MOTE_COUNT);
  const baseZs      = new Float32Array(MOTE_COUNT);
  const spinDirs    = new Float32Array(MOTE_COUNT);
  const seeds       = new Float32Array(MOTE_COUNT);
  const phases      = new Float32Array(MOTE_COUNT);
  const lifeMults   = new Float32Array(MOTE_COUNT);

  for (let i = 0; i < MOTE_COUNT; i++) {
    startAngles[i] = Math.random() * Math.PI * 2;
    // Bias the orbit distribution: sqrt(random) gives more particles
    // at outer radii than inner, mirroring how dust actually rides
    // a vortex (eye is sparser, sheets are denser).
    orbitRadii[i] = MOTE_ORBIT_MIN + Math.sqrt(Math.random()) * (MOTE_ORBIT_MAX - MOTE_ORBIT_MIN);
    // Spread base Z across the thin stack-safe band above the card.
    baseZs[i] = CARD_THICKNESS / 2 + 0.02 + Math.random() * 0.16;
    // 80% spin clockwise for a cohesive vortex direction; 20%
    // counter for some chaos and visual interest.
    spinDirs[i] = Math.random() < 0.8 ? 1 : -1;
    seeds[i] = Math.random();
    phases[i] = Math.random();
    // Wide lifetime variance so the 26-mote cycle is unreadable.
    lifeMults[i] = 0.65 + Math.random() * 0.7;
  }

  moteGeometry.setAttribute('aStartAngle',  new THREE.InstancedBufferAttribute(startAngles, 1));
  moteGeometry.setAttribute('aOrbitRadius', new THREE.InstancedBufferAttribute(orbitRadii, 1));
  moteGeometry.setAttribute('aBaseZ',       new THREE.InstancedBufferAttribute(baseZs, 1));
  moteGeometry.setAttribute('aSpinDir',     new THREE.InstancedBufferAttribute(spinDirs, 1));
  moteGeometry.setAttribute('aSeed',        new THREE.InstancedBufferAttribute(seeds, 1));
  moteGeometry.setAttribute('aPhase',       new THREE.InstancedBufferAttribute(phases, 1));
  moteGeometry.setAttribute('aLifeMult',    new THREE.InstancedBufferAttribute(lifeMults, 1));
  moteGeometry.instanceCount = MOTE_COUNT;

  const moteMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: tSeed },
      uLifetime: { value: MOTE_LIFETIME },
      uSizeMin: { value: MOTE_SIZE_MIN },
      uSizeMax: { value: MOTE_SIZE_MAX },
      // Tornado orbit speed. Radians per second. 0.7 is calm
      // (one full orbit per ~9 seconds); bump to 1.5+ for a
      // dramatic spin.
      uOrbitSpeed: { value: 0.7 },
      // Gust pulse rate. 0.4 gives wave peaks roughly every 16
      // seconds; combined with per-particle phase offset (aSeed)
      // the swarm visibility undulates organically.
      uGustFreq: { value: 0.4 },
      uCardSize: { value: new THREE.Vector2(CARD_WIDTH, CARD_HEIGHT) },
      uTexture: { value: getMoteTexture() },
      uColorCore: { value: new THREE.Color(0xf0f6fc) },
      uColorTint: { value: new THREE.Color(0xc8dceb) },
    },
    vertexShader: MOTE_VERT,
    fragmentShader: MOTE_FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.NormalBlending,
  });

  const motes = new THREE.Mesh(moteGeometry, moteMaterial);
  motes.frustumCulled = false;
  motes.renderOrder = 30;
  group.add(motes);

  return {
    points: group,
    swirlMaterials,
    swirlGeometry,
    moteMaterial,
    moteGeometry,
  };
}

const _tmpVec3 = new THREE.Vector3();
const _tmpEuler = new THREE.Euler();
export function updateAirborneWindEffect(effect, dt, cardMesh) {
  if (!effect || !cardMesh) return;

  for (const material of effect.swirlMaterials) {
    material.uniforms.uTime.value += dt;
  }
  effect.moteMaterial.uniforms.uTime.value += dt;

  cardMesh.getWorldPosition(_tmpVec3);
  effect.points.position.copy(_tmpVec3);

  const intent = cardMesh.userData?.intentEuler;
  const yaw = intent ? intent.z : 0;
  _tmpEuler.set(-Math.PI / 2, 0, yaw, 'XYZ');
  effect.points.rotation.copy(_tmpEuler);
}

export function disposeAirborneWindEffect(effect) {
  if (!effect) return;
  effect.points.parent?.remove(effect.points);
  effect.swirlGeometry?.dispose();
  for (const material of effect.swirlMaterials || []) {
    material.dispose();
  }
  effect.moteGeometry?.dispose();
  effect.moteMaterial?.dispose();
}
